
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Eye,
    Search,
    Filter,
    RotateCcw,
    Download,
    Building2,
    ShieldCheck,
    FileText,
    ExternalLink,
    X,
} from "lucide-react";
import * as XLSX from "xlsx";
import "./admin.css";

import {
    getAllPartners,
    getCompanyDetailsByPartnerId,
    updateCompanyVerificationStatus,
} from "../services/PartnerService";

const FILE_BASE_URL = "/";

const IMAGE_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
const PDF_REGEX = /\.pdf(\?.*)?$/i;

const DOC_KEY_REGEX =
    /certificate|document|doc|file|upload|proof|attachment|image|photo|logo|pan|gst|cin|registration|licen[cs]e|path|url/i;

const NOT_DOC_KEY_REGEX =
    /website|web_?site|email|mobile|phone|address|date|number|no$/i;

const normalizeStatus = (value) =>
    value ? String(value).trim().toUpperCase() : "PENDING";
const isImageFile = (name = "") =>
  /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);

const isPdfFile = (name = "") =>
  /\.pdf$/i.test(name);


const getBase64Mime = (text) => {
    if (
        typeof text !== "string" ||
        text.length < 200 ||
        !/^[A-Za-z0-9+/=\s]+$/.test(text)
    ) {
        return null;
    }

    if (text.startsWith("/9j/")) return "image/jpeg";
    if (text.startsWith("iVBOR")) return "image/png";
    if (text.startsWith("R0lGOD")) return "image/gif";
    if (text.startsWith("UklGR")) return "image/webp";
    if (text.startsWith("JVBER")) return "application/pdf";

    return null;
};

const pickFileString = (value) => {
    if (typeof value === "string") return value.trim();

    if (value && typeof value === "object" && !Array.isArray(value)) {
        const found = [
            "filePath",
            "path",
            "url",
            "fileUrl",
            "location",
            "fileName",
            "name",
        ].find((key) => typeof value[key] === "string" && value[key].trim());

        return found ? value[found].trim() : "";
    }

    return "";
};

const isDocumentField = (key, text) => {
    if (!text) return false;

    if (
        IMAGE_REGEX.test(text) ||
        PDF_REGEX.test(text) ||
        /^data:(image\/|application\/pdf)/i.test(text) ||
        getBase64Mime(text)
    ) {
        return true;
    }

    return (
        DOC_KEY_REGEX.test(key) &&
        !NOT_DOC_KEY_REGEX.test(key) &&
        (/[\/\\]/.test(text) || /\.[a-z0-9]{2,5}$/i.test(text))
    );
};

const getFileUrl = (text) => {
    const mime = getBase64Mime(text);

    if (mime) {
        return `data:${mime};base64,${text.replace(/\s/g, "")}`;
    }

    if (/^(https?:|data:|blob:)/i.test(text)) return text;

    return FILE_BASE_URL + text.replace(/\\/g, "/").replace(/^\/+/, "");
};

const buildDocuments = (record, partnerId) => {
    if (!record || !partnerId) return [];

    const documentConfigs = [
        {
            key: "logoFileName",
            label: "Company Logo",
            endpoint: "logo",
        },
        {
            key: "gstCertificateFileName",
            label: "GST Certificate",
            endpoint: "gst",
        },
        {
            key: "panCardFileName",
            label: "PAN Card",
            endpoint: "pan",
        },
        {
            key: "companyRegistrationFileName",
            label: "Company Registration",
            endpoint: "registration-document",
        },
    ];

    return documentConfigs
        .filter(({ key }) => {
            const value = record[key];
            return typeof value === "string" && value.trim().length > 0;
        })
        .map((doc) => ({
            key: doc.key,
            rootKey: doc.key,
            label: doc.label,
            title: doc.label,
            name: record[doc.key],
            fileName: record[doc.key],
            url: `${FILE_BASE_URL}api/company-details/partner/${partnerId}/${doc.endpoint}`,
        }));
};

const COMPANY_SECTIONS = [
    {
        title: "Company Information",
        fields: [
            [["companyName", "company_name", "legalName", "organizationName"], "Company Name"],
            [["companyType", "company_type", "organizationType", "type"], "Company Type"],
            [["tradeName", "brandName"], "Trade / Brand Name"],
            [["registrationNumber", "registrationNo", "regNo"], "Registration Number"],
            [["cin", "cinNumber"], "CIN"],
            [["incorporationDate", "dateOfIncorporation", "establishedDate", "establishedYear"], "Date of Incorporation"],
            [["website", "websiteUrl"], "Website"],
        ],
    },
    {
        title: "Tax Details",
        fields: [
            [["gst", "gstNumber", "gstNo", "gstin"], "GST Number"],
            [["pan", "panNumber", "panNo"], "PAN Number"],
            [["tan", "tanNumber"], "TAN Number"],
        ],
    },
    {
        title: "Registered Address",
        fields: [
            [["address", "registeredAddress", "companyAddress"], "Address"],
            [["city"], "City"],
            [["state"], "State"],
            [["pincode", "pinCode", "zip", "zipCode"], "Pincode"],
        ],
    },
    {
        title: "Authorized Person",
        fields: [
            [["authorizedPerson", "authorisedPerson", "contactPerson", "name"], "Name"],
            [["designation"], "Designation"],
            [["email", "emailId"], "Email"],
            [["mobile", "phone", "contactNumber"], "Mobile"],
        ],
    },
    {
        title: "Bank Details",
        fields: [
            [["bankName"], "Bank Name"],
            [["accountHolderName", "accountHolder"], "Account Holder Name"],
            [["accountNumber", "accountNo"], "Account Number"],
            [["ifsc", "ifscCode"], "IFSC Code"],
            [["branch", "branchName"], "Branch"],
        ],
    },
];

const normKey = (key) =>
    String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

const toLabel = (key) =>
    String(key)
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (character) => character.toUpperCase());

const formatValue = (value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null || value === undefined || value === "") return "-";

    if (
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)
    ) {
        const date = new Date(value);

        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        }
    }

    return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const buildCompanySections = (record) => {
    const entries = Object.entries(record || {});
    const used = new Set();
    const docKeys = new Set(buildDocuments(record).map((doc) => doc.rootKey));

    const sections = COMPANY_SECTIONS.map((section) => ({
        title: section.title,
        fields: section.fields
            .map(([aliases, label]) => {
                const match = entries.find(
                    ([key]) =>
                        !used.has(key) &&
                        !docKeys.has(key) &&
                        aliases.some((alias) => normKey(alias) === normKey(key))
                );

                if (!match) return null;

                used.add(match[0]);

                return {
                    key: match[0],
                    label,
                    value: formatValue(match[1]),
                };
            })
            .filter(Boolean),
    })).filter((section) => section.fields.length > 0);

    const hidden = [
        "id",
        "partnerid",
        "partner",
        "status",
        "verificationstatus",
        "remark",
        "createdat",
        "updatedat",
    ];

    const others = entries
        .filter(
            ([key]) =>
                !used.has(key) &&
                !docKeys.has(key) &&
                !hidden.includes(normKey(key)) &&
                !/password|token/i.test(key)
        )
        .map(([key, value]) => ({
            key,
            label: toLabel(key),
            value: formatValue(value),
        }));

    if (others.length) {
        sections.push({ title: "Other Details", fields: others });
    }

    return sections;
};


function DocCard({ doc, onPreview, onDownload }) {
    const fileName = String(doc.fileName || doc.name || "").trim();

    const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
    const isPdf = /\.pdf$/i.test(fileName);

    return (
        <div className="document-card">
            <h4>{doc.label || doc.title}</h4>

            <p className="document-filename">{fileName}</p>

            {isImage ? (
                <img
                    src={doc.url}
                    alt={doc.label || doc.title}
                    className="document-preview"
                />
            ) : isPdf ? (
                <iframe
                    src={doc.url}
                    title={doc.label || doc.title}
                    className="document-preview"
                />
            ) : (
                <p>
                    Preview is unavailable for this file type.
                    Please download the file.
                </p>
            )}

            <div className="document-actions">
                {(isImage || isPdf) && (
                    <button
                        type="button"
                        onClick={() => onPreview?.(doc)}
                    >
                        <Eye size={14} /> Preview
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => onDownload?.(doc)}
                >
                    <Download size={14} /> Download
                </button>
            </div>
        </div>
    );
}

function RecruitmentRequests() {
    const navigate = useNavigate();

    const [partners, setPartners] = useState([]);
    const [filteredPartners, setFilteredPartners] = useState([]);
    const [verificationStatuses, setVerificationStatuses] = useState({});
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [companyType, setCompanyType] = useState("");
    const [city, setCity] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 6;

    const [companyPartner, setCompanyPartner] = useState(null);
    const [companyData, setCompanyData] = useState(null);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyError, setCompanyError] = useState("");

    const [verifyStatus, setVerifyStatus] = useState("PENDING");
    const [verifyRemark, setVerifyRemark] = useState("");
    const [verifySaving, setVerifySaving] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);

    const companyRequestRef = useRef(0);

    // Handles both direct responses and { data: ... } API responses.
    const findCompanyRecord = (responseData, partnerId) => {
        let body = responseData;

        // Unwrap common Spring/API response wrappers.
        for (let i = 0; i < 3; i += 1) {
            if (
                body &&
                !Array.isArray(body) &&
                typeof body === "object" &&
                body.data !== undefined
            ) {
                body = body.data;
            } else {
                break;
            }
        }

        if (Array.isArray(body)) {
            return (
                body.find(
                    (item) =>
                        String(
                            item.partnerId ??
                            item.partner_id ??
                            item.partner?.id ??
                            ""
                        ) === String(partnerId)
                ) || null
            );
        }

        return body && typeof body === "object" ? body : null;
    };

    const fetchPartners = useCallback(async () => {
        try {
            setLoading(true);

            const response = await getAllPartners();
            console.log("PARTNER LIST API RESPONSE:", response.data);

            const apiData = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];

            const approvedPartners = apiData
                .filter(
                    (partner) =>
                        normalizeStatus(partner.status) === "APPROVED"
                )
                .map((partner) => ({ ...partner, status: "APPROVED" }));

            setPartners(approvedPartners);

            // Load each approved partner's company verification status.
            const statusEntries = await Promise.all(
                approvedPartners.map(async (partner) => {
                    try {
                        const companyResponse =
                            await getCompanyDetailsByPartnerId(partner.id);

                        const record = findCompanyRecord(
                            companyResponse.data,
                            partner.id
                        );

                        const status = normalizeStatus(
                            record?.verificationStatus
                        );

                        return [
                            partner.id,
                            status === "VERIFIED" ? "VERIFIED" : "PENDING",
                        ];
                    } catch (error) {
                        console.error(
                            "Verification status fetch failed:",
                            partner.id,
                            error
                        );

                        return [partner.id, "PENDING"];
                    }
                })
            );

            setVerificationStatuses(Object.fromEntries(statusEntries));
        } catch (error) {
            console.error("PARTNER LIST FETCH ERROR:", error);
            console.error("SERVER RESPONSE:", error.response?.data);

            alert(
                error.response?.data?.message ||
                "Unable to load approved partners."
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPartners();
    }, [fetchPartners]);

    useEffect(() => {
        let data = [...partners];

        if (search.trim()) {
            const searchText = search.toLowerCase().trim();

            data = data.filter((partner) =>
                [
                    partner.companyName,
                    partner.name,
                    partner.email,
                    partner.mobile,
                    partner.gst,
                    partner.pan,
                    partner.city,
                ].some((value) =>
                    String(value || "").toLowerCase().includes(searchText)
                )
            );
        }

        if (companyType) {
            data = data.filter(
                (partner) => String(partner.companyType || "") === companyType
            );
        }

        if (city.trim()) {
            const cityText = city.toLowerCase().trim();

            data = data.filter((partner) =>
                String(partner.city || "").toLowerCase().includes(cityText)
            );
        }

        setFilteredPartners(data);
        setCurrentPage(1);
    }, [search, companyType, city, partners]);

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

    const currentPartners = filteredPartners.slice(
        indexOfFirstRecord,
        indexOfLastRecord
    );

    const totalPages = Math.ceil(filteredPartners.length / recordsPerPage);

    const changePage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const clearFilter = () => {
        setSearch("");
        setCompanyType("");
        setCity("");
        setCurrentPage(1);
    };

    const handleView = (id) => {
        navigate(`/partners/view/${id}`);
    };

    const loadCompanyDetails = async (partner, requestId) => {
        const response = await getCompanyDetailsByPartnerId(partner.id);

        if (companyRequestRef.current !== requestId) return;

        const record = findCompanyRecord(response.data, partner.id);
        console.log(
    "COMPANY DOCUMENTS FOUND:",
    buildDocuments(record, partner.id)
);

        console.log("COMPANY DETAILS RAW:", response.data);
        console.log("COMPANY DOCUMENTS FOUND:", buildDocuments(record));

        setCompanyData(record);

        const currentStatus = normalizeStatus(record?.verificationStatus);
        const safeStatus =
            currentStatus === "VERIFIED" ? "VERIFIED" : "PENDING";

        setVerifyStatus(safeStatus);
        setVerifyRemark(record?.remark || "");

        setVerificationStatuses((previous) => ({
            ...previous,
            [partner.id]: safeStatus,
        }));
    };

    const handleCompanyDetails = async (partner) => {
        const requestId = companyRequestRef.current + 1;
        companyRequestRef.current = requestId;

        setCompanyPartner(partner);
        setCompanyData(null);
        setCompanyError("");
        setCompanyLoading(true);
        setVerifyStatus("PENDING");
        setVerifyRemark("");
        setPreviewDoc(null);

        try {
            await loadCompanyDetails(partner, requestId);
        } catch (error) {
            if (companyRequestRef.current !== requestId) return;

            console.error("COMPANY DETAILS FETCH ERROR:", error);

            if (error.response?.status === 404) {
                setCompanyData(null);
                setCompanyError(
                    "No company details found for this partner."
                );
            } else {
                setCompanyError(
                    error.response?.data?.message ||
                    "Unable to load company details."
                );
            }
        } finally {
            if (companyRequestRef.current === requestId) {
                setCompanyLoading(false);
            }
        }
    };

    const closeCompanyDetails = () => {
        companyRequestRef.current += 1;

        setCompanyPartner(null);
        setCompanyData(null);
        setCompanyError("");
        setCompanyLoading(false);
        setVerifyStatus("PENDING");
        setVerifyRemark("");
        setVerifySaving(false);
        setPreviewDoc(null);
    };

    const handleSaveVerification = async () => {
        if (!companyPartner || !companyData || verifySaving) return;

        try {
            setVerifySaving(true);

            const partnerId = companyPartner.id;
            const requestedStatus =
                normalizeStatus(verifyStatus) === "VERIFIED"
                    ? "VERIFIED"
                    : "PENDING";

            // IMPORTANT: backend VerificationRequest expects "status", not
            // "verificationStatus".
            const response = await updateCompanyVerificationStatus(
                partnerId,
                {
                    status: requestedStatus,
                    remark: verifyRemark.trim() || null,
                }
            );

            console.log("VERIFICATION UPDATE RESPONSE:", response.data);

            // Update the main table immediately after a successful save.
            setVerificationStatuses((previous) => ({
                ...previous,
                [partnerId]: requestedStatus,
            }));

            // Reflect the saved values in the modal immediately.
            setCompanyData((previous) => ({
                ...previous,
                verificationStatus: requestedStatus,
                remark: verifyRemark.trim() || null,
            }));

            setVerifyStatus(requestedStatus);

            // Re-fetch persisted data to confirm what the backend saved.
            try {
                const result =
                    await getCompanyDetailsByPartnerId(partnerId);

                const updatedRecord = findCompanyRecord(
                    result.data,
                    partnerId
                );

                if (updatedRecord) {
                    const savedStatus =
                        normalizeStatus(updatedRecord.verificationStatus) ===
                        "VERIFIED"
                            ? "VERIFIED"
                            : "PENDING";

                    setCompanyData(updatedRecord);
                    setVerifyStatus(savedStatus);
                    setVerifyRemark(updatedRecord.remark || "");

                    setVerificationStatuses((previous) => ({
                        ...previous,
                        [partnerId]: savedStatus,
                    }));
                }
            } catch (reloadError) {
                // The PUT succeeded; keep the immediate UI update if reload fails.
                console.error(
                    "Verification saved, but reloading details failed:",
                    reloadError
                );
            }

            alert("Verification status updated successfully!");
        } catch (error) {
            console.error("VERIFICATION UPDATE ERROR:", error);
            console.error("SERVER RESPONSE:", error.response?.data);

            alert(
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Failed to update verification status."
            );
        } finally {
            setVerifySaving(false);
        }
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                if (previewDoc) {
                    setPreviewDoc(null);
                } else {
                    closeCompanyDetails();
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [previewDoc]);

    useEffect(() => {
        document.body.style.overflow = companyPartner ? "hidden" : "";

        return () => {
            document.body.style.overflow = "";
        };
    }, [companyPartner]);

    const handleExport = () => {
        if (filteredPartners.length === 0) {
            alert("No approved partner data available.");
            return;
        }

        const exportData = filteredPartners.map((partner, index) => ({
            "S.No": index + 1,
            "Company Name": partner.companyName || "",
            "Company Type": partner.companyType || "",
            "Contact Person": partner.name || "",
            Designation: partner.designation || "",
            Email: partner.email || "",
            Mobile: partner.mobile || "",
            GST: partner.gst || "",
            PAN: partner.pan || "",
            Address: partner.address || "",
            City: partner.city || "",
            State: partner.state || "",
            Pincode: partner.pincode || "",
            Website: partner.website || "",
            "Partner Status": "Approved",
            "Verification Status":
                verificationStatuses[partner.id] === "VERIFIED"
                    ? "Verified"
                    : "Pending",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Approved Partners"
        );

        XLSX.writeFile(workbook, "Approved_Partner_List.xlsx");
    };

   const companyDocuments = companyData
    ? buildDocuments(companyData, companyPartner?.id)
    : [];

    const handleDownloadDoc = async (doc) => {
        try {
            const response = await fetch(doc.url);

            if (!response.ok) throw new Error("Download failed");

            const blob = await response.blob();
            const href = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = href;
            link.download = doc.name;
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("DOCUMENT DOWNLOAD ERROR:", error);
            window.open(doc.url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div className="partner-page">
            <div className="partner-header">
                <div className="partner-header-left">
                    <div className="partner-title-icon">
                        <Eye size={20} />
                    </div>

                    <div>
                        <h1>Partner List</h1>
                        <p>Manage all approved training partners</p>
                    </div>
                </div>

                <div className="partner-header-actions">
                    <button
                        type="button"
                        className="partner-export-btn"
                        onClick={handleExport}
                    >
                        <Download size={15} />
                        Export Excel
                    </button>
                </div>
            </div>

            <div className="partner-filter-card">
                <div className="partner-filter-title">
                    <div className="partner-filter-icon">
                        <Filter size={16} />
                    </div>

                    <div>
                        <h3>Partner Filters</h3>
                        <span>Search approved partners</span>
                    </div>
                </div>

                <div className="partner-filter-grid">
                    <div className="partner-search">
                        <Search size={15} />
                        <input
                            type="text"
                            placeholder="Search Company / Name / Email / Mobile"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </div>

                    <select
                        value={companyType}
                        onChange={(event) =>
                            setCompanyType(event.target.value)
                        }
                    >
                        <option value="">All Company Types</option>
                        <option value="Training Partner">Training Partner</option>
                        <option value="Training Company">Training Company</option>
                        <option value="Corporate">Corporate</option>
                        <option value="Institute">Institute</option>
                        <option value="Consultancy">Consultancy</option>
                        <option value="Other">Other</option>
                    </select>

                    <input
                        className="partner-simple-input"
                        type="text"
                        placeholder="Filter by City"
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                    />

                    <button
                        type="button"
                        className="partner-clear-btn"
                        onClick={clearFilter}
                    >
                        <RotateCcw size={14} />
                        Clear
                    </button>
                </div>
            </div>

            <div className="partner-table-card">
                <div className="partner-table-header">
                    <div>
                        <h2>Approved Partner List</h2>
                        <span>
                            Showing {filteredPartners.length} approved partners
                        </span>
                    </div>

                    {totalPages > 0 && (
                        <div className="trainer-pagination">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => changePage(currentPage - 1)}
                            >
                                Prev
                            </button>

                            {Array.from({ length: totalPages }, (_, index) => (
                                <button
                                    type="button"
                                    key={index}
                                    className={
                                        currentPage === index + 1 ? "active" : ""
                                    }
                                    onClick={() => changePage(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}

                            <button
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => changePage(currentPage + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                <div className="partner-table-wrapper">
                    {loading ? (
                        <div className="partner-empty">
                            Loading approved partners...
                        </div>
                    ) : currentPartners.length === 0 ? (
                        <div className="partner-empty">
                            No Approved Partner Found
                        </div>
                    ) : (
                        <table className="employee-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Company Details</th>
                                    <th>Contact Person</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                    <th>Verification</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {currentPartners.map((partner, index) => {
                                    const verificationStatus =
                                        verificationStatuses[partner.id] ||
                                        "PENDING";

                                    return (
                                        <tr key={partner.id || index}>
                                            <td>
                                                {indexOfFirstRecord + index + 1}
                                            </td>

                                            <td>
                                                <div className="partner-company-details">
                                                    <strong className="partner-company-name">
                                                        {partner.companyName || "-"}
                                                    </strong>

                                                    <span>
                                                        Type: {partner.companyType || "-"}
                                                    </span>
                                                    <span>
                                                        GST: {partner.gst || "-"}
                                                    </span>
                                                    <span>
                                                        PAN: {partner.pan || "-"}
                                                    </span>

                                                    {partner.website && (
                                                        <a
                                                            href={
                                                                String(partner.website).startsWith("http")
                                                                    ? partner.website
                                                                    : `https://${partner.website}`
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="website-link"
                                                        >
                                                            Visit Website
                                                        </a>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="partner-contact-details">
                                                    <strong>
                                                        {partner.name || "-"}
                                                    </strong>
                                                    <span>
                                                        {partner.designation || "-"}
                                                    </span>
                                                    <span>
                                                        {partner.email || "-"}
                                                    </span>
                                                    <span>
                                                        {partner.mobile || "-"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="partner-address-details">
                                                    <span>
                                                        {partner.address || "-"}
                                                    </span>

                                                    <span>
                                                        {partner.city || ""}
                                                        {partner.city && partner.state
                                                            ? ", "
                                                            : ""}
                                                        {partner.state || ""}
                                                        {!partner.city && !partner.state
                                                            ? "-"
                                                            : ""}
                                                    </span>

                                                    <small>
                                                        Pincode: {partner.pincode || "-"}
                                                    </small>
                                                </div>
                                            </td>

                                            <td>
                                                <span className="partner-status-select approved">
                                                    Approved
                                                </span>
                                            </td>

                                            <td>
                                                <span
                                                    className={`partner-status-select ${
                                                        verificationStatus === "VERIFIED"
                                                            ? "verified"
                                                            : "pending"
                                                    }`}
                                                >
                                                    {verificationStatus === "VERIFIED"
                                                        ? "Verified"
                                                        : "Pending"}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="employee-actions">
                                                    
                                                    <button
                                                        type="button"
                                                        className="small-action-btn view-btn"
                                                        title="Company Verification"
                                                        onClick={() =>
                                                            handleCompanyDetails(partner)
                                                        }
                                                    >
                                                        <Building2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {companyPartner && (
                <div
                    className="cv-overlay"
                    onClick={closeCompanyDetails}
                >
                    <div
                        className="cv-modal"
                        role="dialog"
                        aria-modal="true"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="cv-header">
                            <div className="cv-header-left">
                                <div className="cv-avatar">
                                    <Building2 size={22} />
                                </div>

                                <div>
                                    <h2>Company Verification Form</h2>
                                    <p>
                                        {companyPartner.companyName || "-"}
                                        {" "}(Partner ID: {companyPartner.id})
                                    </p>
                                </div>
                            </div>

                            <div className="cv-header-right">
                                {companyData && (
                                    <span
                                        className={`cv-chip ${
                                            normalizeStatus(
                                                companyData.verificationStatus
                                            ) === "VERIFIED"
                                                ? "verified"
                                                : "review"
                                        }`}
                                    >
                                        {normalizeStatus(
                                            companyData.verificationStatus
                                        ) === "VERIFIED"
                                            ? "Verified"
                                            : "Pending"}
                                    </span>
                                )}

                                <button
                                    type="button"
                                    className="cv-close"
                                    title="Close"
                                    onClick={closeCompanyDetails}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="cv-body">
                            {companyLoading ? (
                                <div className="cv-state">
                                    Loading company details...
                                </div>
                            ) : companyError ? (
                                <div className="cv-state error">
                                    {companyError}
                                </div>
                            ) : !companyData ? (
                                <div className="cv-state">
                                    No company details found for this partner.
                                </div>
                            ) : (
                                <form
                                    onSubmit={(event) => event.preventDefault()}
                                >
                                    {buildCompanySections(companyData).map(
                                        (section) => (
                                            <div
                                                className="cv-section"
                                                key={section.title}
                                            >
                                                <div className="cv-section-title">
                                                    {section.title}
                                                </div>

                                                <div className="cv-grid">
                                                    {section.fields.map((field) => {
                                                        const long =
                                                            field.value.length > 50 ||
                                                            field.value.includes("\n");

                                                        return (
                                                            <div
                                                                key={field.key}
                                                                className={`cv-field${long ? " full" : ""}`}
                                                            >
                                                                <label>
                                                                    {field.label}
                                                                </label>
                                                                <div className="cv-value">
                                                                    {field.value}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )
                                    )}

                                    <div className="cv-section">
                                        <div className="cv-section-title">
                                            <FileText size={16} />
                                            Documents
                                        </div>

                                        {companyDocuments.length === 0 ? (
                                            <div className="cv-docs-empty">
                                                No documents uploaded.
                                            </div>
                                        ) : (
                                            <div className="cv-docs-grid">
                                                {companyDocuments.map((doc) => (
                                                    <DocCard
                                                        key={doc.key}
                                                        doc={doc}
                                                        onPreview={setPreviewDoc}
                                                        onDownload={handleDownloadDoc}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="cv-section cv-verify">
                                        <div className="cv-section-title">
                                            <ShieldCheck size={16} />
                                            Verification Status
                                        </div>

                                        <div className="cv-verify-options">
                                            <label
                                                className={`cv-radio review ${
                                                    verifyStatus === "PENDING"
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="verifyStatus"
                                                    value="PENDING"
                                                    checked={
                                                        verifyStatus === "PENDING"
                                                    }
                                                    onChange={() =>
                                                        setVerifyStatus("PENDING")
                                                    }
                                                />
                                                Pending
                                            </label>

                                            <label
                                                className={`cv-radio verified ${
                                                    verifyStatus === "VERIFIED"
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="verifyStatus"
                                                    value="VERIFIED"
                                                    checked={
                                                        verifyStatus === "VERIFIED"
                                                    }
                                                    onChange={() =>
                                                        setVerifyStatus("VERIFIED")
                                                    }
                                                />
                                                Verified
                                            </label>
                                        </div>

                                        <div className="cv-field full">
                                            <label>Remark (optional)</label>

                                            <textarea
                                                className="cv-textarea"
                                                rows={3}
                                                placeholder="Verification remark likhein..."
                                                value={verifyRemark}
                                                onChange={(event) =>
                                                    setVerifyRemark(
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>

                        <div className="cv-footer">
                            <button
                                type="button"
                                className="cv-btn cv-btn-light"
                                onClick={closeCompanyDetails}
                            >
                                Close
                            </button>

                            <button
                                type="button"
                                className="cv-btn cv-btn-primary"
                                disabled={
                                    !companyData ||
                                    companyLoading ||
                                    verifySaving
                                }
                                onClick={handleSaveVerification}
                            >
                                {verifySaving ? "Saving..." : "Save Verification"}
                            </button>
                        </div>
                    </div>

                    {previewDoc && (
                        <div
                            className="cv-lightbox"
                            onClick={(event) => {
                                event.stopPropagation();
                                setPreviewDoc(null);
                            }}
                        >
                            <div
                                className="cv-lightbox-box"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="cv-lightbox-bar">
                                    <span>{previewDoc.label}</span>

                                    <div className="cv-lightbox-actions">
                                        <button
                                            type="button"
                                            className="cv-doc-btn primary"
                                            onClick={() =>
                                                handleDownloadDoc(previewDoc)
                                            }
                                        >
                                            <Download size={14} />
                                            Download
                                        </button>

                                        <button
                                            type="button"
                                            className="cv-close"
                                            title="Close preview"
                                            onClick={() => setPreviewDoc(null)}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className="cv-lightbox-body">
                                    <img
                                        src={previewDoc.url}
                                        alt={previewDoc.label}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default RecruitmentRequests;
